/**
 * ScenarioSpec — входной контракт authoring-слоя (`POST /api/scenario-generator/*`).
 *
 * Это НЕ то же самое, что `ScenarioTemplate` (строка `scenario_templates`).
 * Spec — авторский исходник: мир + цели + ВЕТКИ (`branches`), каждую из которых
 * генератор реально прогоняет через симуляцию, чтобы получить метрики исхода.
 * Мэппер (`ScenarioSpecMapper`) превращает spec в templatePayload, а publish
 * пишет его в БД. Обратного преобразования на сервере нет — см. `templateToSpec`.
 *
 * Зеркалит backend/src/services/scenario-generator/{contracts,ScenarioSpecValidator}.js.
 */

import i18n from '../i18n';

/** Генератор v1 умеет публиковать только эти действия (PUBLISHABLE_ACTIONS). */
export const PUBLISHABLE_ACTIONS = ['watering', 'fertilizing'] as const;
export type PublishableAction = (typeof PUBLISHABLE_ACTIONS)[number];

export const VALID_TARGET_KINDS = ['field', 'plot', 'bed', 'planting', 'structural_object'] as const;
export type ScenarioTargetKind = (typeof VALID_TARGET_KINDS)[number];

export const VALID_VISIBILITIES = ['draft', 'private', 'public'] as const;
export type ScenarioVisibility = (typeof VALID_VISIBILITIES)[number];

/**
 * Роль ветки в педагогике сценария.
 *
 * Две группы, и смешивать их нельзя. Первые четыре — исторический словарь
 * генератора (эталон / приемлемо / на грани / ошибка). Вторые — словарь СИДОВ,
 * которым описаны ветки контраста, показываемые игроку после финала
 * (`comparison.branches`, см. мануал §2.7). Конвертер пропускает и те и другие:
 * роль здесь декоративна, гейт различимости смотрит на числа, а не на ярлык.
 */
export type BranchRole =
  | 'reference' | 'good' | 'borderline' | 'bad'
  | 'better' | 'worse' | 'alternative' | 'baseline';

export interface ScenarioSpecCell {
  row: number;
  col: number;
  /** Только `soil` пригоден под грядку — валидатор режет остальное. */
  type?: string;
}

export interface ScenarioSpecPlot {
  id: string;
  fieldId?: string | null;
  name?: string;
  northAzimuth?: number;
  cells: ScenarioSpecCell[];
}

export interface ScenarioSpecBed {
  id: string;
  plotId: string;
  name?: string;
  cells: Array<{ row: number; col: number }>;
  soilType?: string;
  initialState?: Record<string, unknown>;
  [key: string]: unknown;
}

export interface ScenarioSpecPlanting {
  id: string;
  bedId: string;
  crop: string;
  quantity?: number;
  initialState?: Record<string, unknown>;
  /** Обязателен: валидатор требует inline-техкарту, резолва по имени тут нет. */
  growthProfile?: Record<string, unknown>;
  [key: string]: unknown;
}

export interface ScenarioSpecWeatherDay {
  date: string;
  tempMin: number;
  tempMax: number;
  precipitationMm?: number;
  humidityPct?: number;
  windSpeed?: number;
}

export interface ScenarioSpecGoal {
  id: string;
  label?: string;
  metric: string;
  operator?: 'gte' | 'lte' | 'gt' | 'lt' | 'eq' | 'between';
  target: unknown;
  actionCount?: { action: string; min?: number; max?: number; exact?: number };
}

export interface ScenarioSpecScoring {
  tiers: Array<{ tier: number; minPassed: number }>;
}

export interface ScenarioSpecWorld {
  startDate: string;
  timezone?: string;
  durationDays: number;
  expectedEvents?: number;
  sessionConfig?: Record<string, unknown>;
  weatherSeries: ScenarioSpecWeatherDay[];
  fields?: Array<{ id: string; name?: string }>;
  plots: ScenarioSpecPlot[];
  beds: ScenarioSpecBed[];
  plantings: ScenarioSpecPlanting[];
  structuralObjects?: unknown[];
  goals: ScenarioSpecGoal[];
  scoring?: ScenarioSpecScoring;
  comparison?: { metrics?: string[] } | null;
  narrative?: { intro?: string; objective?: string; hint?: string };
}

export interface ScenarioSpecActionDefinition {
  action: PublishableAction | string;
  targetKinds: ScenarioTargetKind[];
  uiLabel?: string;
}

export interface ScenarioSpecPlannedAction {
  dateOffsetDays: number;
  action: PublishableAction | string;
  target: { kind: ScenarioTargetKind | string; id: string };
  payload?: Record<string, unknown>;
}

export interface ScenarioSpecBranch {
  branchId: string;
  label?: string;
  role?: BranchRole;
  actionTape: ScenarioSpecPlannedAction[];
}

export interface ScenarioSpec {
  specVersion: number;
  scenarioId: string;
  title: string;
  slug: string;
  kind: string;
  version?: number;
  scenarioTemplateId?: string;
  allowRetroEdit?: boolean;
  metadata?: { tags?: string[]; [key: string]: unknown };
  pedagogy: {
    learningObjectives?: string[];
    targetAudience?: string;
    /** true → quality gate потребует ветку с role='reference'. */
    requireReferenceComparison?: boolean;
  };
  generationPolicy?: { mode?: 'sim'; publishTarget?: string; validateBeforePublish?: boolean };
  actionsCatalog: ScenarioSpecActionDefinition[];
  world: ScenarioSpecWorld;
  branches: ScenarioSpecBranch[];
  /**
   * Награды шаблона: натуральные, лесенкой по ленте — `firstClear[tier]`
   * открывает сорта. Денег на аккаунте нет: покупки живут внутри прохождения,
   * за его собственную валюту (см. `economy` ниже).
   */
  rewards?: Record<string, unknown> | null;
  /**
   * Экономика ПРОХОЖДЕНИЯ: бюджет одной партии, а не кошелёк аккаунта
   * (docs/SCENARIO_ATTEMPT_ECONOMY.md). `startingBalance`, `prices.sell`,
   * `catalog`, `battery`. Отсутствие блока — умолчание: задание играется на
   * ограниченных запасах без денег.
   */
  economy?: Record<string, unknown> | null;
}

// ── Ответы генератора ──────────────────────────────────────────────────────

/** Одна ошибка валидации. `path` указывает в spec — им подсвечиваем поле. */
export interface ScenarioQualityIssue {
  code: string;
  path: string;
  message: string;
  action?: string | null;
  target_kind?: string | null;
  target_id?: string | null;
  [key: string]: unknown;
}

export interface ScenarioBranchOutcome {
  branchId: string;
  label?: string;
  role?: string;
  terminal: boolean;
  metrics: Record<string, number>;
  goalsProgress?: { completed?: boolean; passedCount?: number; total?: number } | null;
  meta: {
    source: string;
    engineVersion: string;
    modelVersion: string | null;
    confidence: number | null;
    verifiedBySim: boolean;
  };
}

export interface ScenarioQualityReport {
  publishable: boolean;
  errors: ScenarioQualityIssue[];
  warnings: ScenarioQualityIssue[];
  specValidation?: { ok: boolean; errors: ScenarioQualityIssue[]; warnings: ScenarioQualityIssue[] };
  templateValidation?: { ok?: boolean; errors: ScenarioQualityIssue[] };
  summary?: { branchCount: number; metricNames: string[] };
}

export interface ScenarioProvenance {
  generationMode: string;
  generatedAt: string;
  sourceSpecHash: string;
  evaluatorPolicyHash: string;
  generatorVersion: string;
  engineVersion: string;
  modelVersion: string | null;
  simulationContractVersion: number;
  eventSchemaVersion: number;
  runtimeContractVersion: number;
  branchProvenance: Array<Record<string, unknown>>;
}

export interface ScenarioDryRunResponse {
  valid: boolean;
  templatePayload: Record<string, unknown> | null;
  outcomes: ScenarioBranchOutcome[];
  qualityReport: ScenarioQualityReport;
  provenance: ScenarioProvenance | null;
  warnings: ScenarioQualityIssue[];
}

export interface ScenarioPublishResponse extends ScenarioDryRunResponse {
  published: true;
  /** true → такой spec уже публиковался, новой версии не создано. */
  idempotent: boolean;
  scenarioTemplate: {
    scenarioTemplateId: string;
    version: number;
    slug: string;
    title: string;
    kind: string;
    visibility: string;
  };
}

export interface ScenarioGeneratorRequest {
  spec: ScenarioSpec;
  visibility?: ScenarioVisibility;
  generationPolicy?: { mode?: 'sim' };
}

// ── seed → spec ────────────────────────────────────────────────────────────

/**
 * Причина, по которой канонический seed нельзя опубликовать как есть.
 * Генератор v1 знает только полив и подкормку, поэтому сценарии со сбором
 * (`harvest-day`, `radish-rush`) конвертируются, но ветку с harvest теряют.
 */
export interface SpecConversionNote {
  severity: 'warning' | 'error';
  message: string;
}

export interface SpecConversionResult {
  spec: ScenarioSpec;
  notes: SpecConversionNote[];
}

const isPublishable = (action: unknown): action is PublishableAction =>
  (PUBLISHABLE_ACTIONS as readonly string[]).includes(String(action));

/**
 * Строит ScenarioSpec из шаблона-семени (`GET /api/scenario-debug/templates`).
 *
 * Это единственный путь превратить семь канонических сценариев, живущих только
 * в backend/src/data/seeds/scenarioTemplates.js, в строки `scenario_templates` —
 * то есть в то, что видит игровая галерея. Конвертация не серверная и намеренно
 * консервативна: всё, что генератор v1 не умеет, возвращается в `notes`, а не
 * молча выбрасывается.
 */
export const templateToSpec = (
  template: {
    scenarioTemplateId?: string;
    version?: number;
    slug: string;
    title: string;
    kind: string;
    durationDays: number;
    allowRetroEdit?: boolean;
    worldSpec: Record<string, unknown>;
    goals?: ScenarioSpecGoal[];
    scoring?: ScenarioSpecScoring;
    narrative?: Record<string, unknown>;
    comparison?: Record<string, unknown> | null;
    rewards?: Record<string, unknown> | null;
    economy?: Record<string, unknown> | null;
  },
): SpecConversionResult => {
  const notes: SpecConversionNote[] = [];
  const world = (template.worldSpec || {}) as Record<string, any>;

  const plots = (world.plots || []) as ScenarioSpecPlot[];
  const beds = (world.beds || []) as ScenarioSpecBed[];
  const plantings = (world.plantings || []) as ScenarioSpecPlanting[];

  // Валидатор ругается на битую ссылку plot→field только если fields непустой,
  // но мир без поля потом неудобно править руками — синтезируем одно.
  const fieldId = 'field_1';

  plantings.forEach((planting, index) => {
    if (!planting.growthProfile && !(planting as any).growth_profile) {
      notes.push({
        severity: 'error',
        message: i18n.t('scenarios_admin:spec.notes.plantingWithoutGrowthProfile', { index, id: planting.id }),
      });
    }
  });

  plots.forEach((plot, index) => {
    const nonSoil = (plot.cells || []).filter((cell) => cell.type && cell.type !== 'soil').length;
    if (nonSoil > 0) {
      notes.push({
        severity: 'warning',
        message: i18n.t('scenarios_admin:spec.notes.plotNonSoilCells', { index, id: plot.id, count: nonSoil }),
      });
    }
  });

  // Ветки — единственный источник outcomes; без них quality gate падает на
  // outcomes_missing. Форма эталона у семян ПЛОСКАЯ (`comparison.referenceEvents`),
  // а не список веток: `comparison.branches` — это уже выход генератора. Читаем оба,
  // иначе авторская «правильная игра» пяти сценариев молча теряется и все ветки
  // выходят одинаковыми.
  const comparison = (template.comparison || {}) as Record<string, any>;

  const convertTape = (tape: Array<Record<string, any>>, tapeLabel: string): ScenarioSpecPlannedAction[] => {
    const converted: ScenarioSpecPlannedAction[] = [];
    const droppedActions: string[] = [];
    (tape || []).forEach((step) => {
      const action = step.action;
      if (!isPublishable(action)) {
        droppedActions.push(String(action));
        return;
      }
      const target = step.scenarioTarget || step.target || {};
      const payload: Record<string, unknown> = {};
      if (step.depthMm != null) payload.wateringDepthMm = step.depthMm;
      if (step.fertilizerKind) payload.fertilizerKind = step.fertilizerKind;
      if (step.quantityAffected != null) payload.quantityAffected = step.quantityAffected;
      converted.push({
        dateOffsetDays: Number(step.dateOffsetDays ?? step.date_offset_days ?? 0),
        action: String(action),
        target: { kind: String(target.kind || 'bed'), id: String(target.id) },
        ...(Object.keys(payload).length ? { payload } : {}),
      });
    });
    if (droppedActions.length) {
      notes.push({
        severity: 'warning',
        message: i18n.t('scenarios_admin:spec.notes.droppedActions', {
          tape: tapeLabel,
          dropped: droppedActions.join(', '),
          publishable: PUBLISHABLE_ACTIONS.join(', '),
        }),
      });
    }
    return converted;
  };

  const branches: ScenarioSpecBranch[] = [
    { branchId: 'baseline', label: i18n.t('scenarios_admin:spec.branch.baselineLabel'), role: 'borderline', actionTape: [] },
  ];

  const referenceEvents = (comparison.referenceEvents || comparison.reference_events || []) as Array<Record<string, any>>;
  if (referenceEvents.length) {
    branches.push({
      branchId: 'reference',
      label: String(comparison.referenceLabel || comparison.reference_label || i18n.t('scenarios_admin:spec.branch.referenceLabel')),
      role: 'reference',
      actionTape: convertTape(referenceEvents, i18n.t('scenarios_admin:spec.tape.reference')),
    });
  }

  ((comparison.branches || []) as Array<Record<string, any>>).forEach((branch, index) => {
    const branchId = String(branch.branchId || branch.id || `branch_${index + 1}`);
    if (branches.some((existing) => existing.branchId === branchId)) return;
    branches.push({
      branchId,
      label: branch.label || branch.title,
      role: (branch.role as BranchRole) || undefined,
      // ВАЖНО: `eventsOverride` первым. Сиды и дизайн-корпус описывают ветку
      // именно так; без этого ключа ветка молча приезжает с ПУСТОЙ лентой и
      // становится неотличима от baseline — dry-run при этом зелёный, потому
      // что бэкендский гейт валит только полное схлопывание ВСЕХ веток.
      actionTape: convertTape(
        branch.eventsOverride || branch.events || branch.actionTape || branch.action_tape || [],
        i18n.t('scenarios_admin:spec.tape.branch', { branchId }),
      ),
    });
  });

  if (branches.length === 1) {
    notes.push({
      severity: 'warning',
      message: i18n.t('scenarios_admin:spec.notes.noReferenceTape'),
    });
  }

  // Метрики сравнения берём авторские. Цели часто адресуют сущность точечным
  // путём (`plantings.planting_1.healthScore`), а quality gate ищет метрику
  // ПЛОСКИМ ключом — подставив сюда метрики целей, мы бы сравнивали undefined с
  // undefined и получали ложное «ветки неразличимы» на сценарии, где ветки
  // расходятся вдвое.
  const authoredMetrics = ((template.comparison as any)?.metrics || []) as string[];
  const comparisonMetrics = authoredMetrics.length
    ? authoredMetrics
    : (template.goals || []).map((goal) => goal.metric).filter((metric) => Boolean(metric) && !metric.includes('.'));

  if (!authoredMetrics.length && comparisonMetrics.length === 0 && (template.goals || []).length > 0) {
    notes.push({
      severity: 'warning',
      message: i18n.t('scenarios_admin:spec.notes.noComparisonMetrics'),
    });
  }

  const bedIds = beds.map((bed) => String(bed.id));
  const actionsCatalog: ScenarioSpecActionDefinition[] = [
    { action: 'watering', targetKinds: ['bed'], uiLabel: i18n.t('scenarios_admin:spec.action.watering') },
    { action: 'fertilizing', targetKinds: ['bed'], uiLabel: i18n.t('scenarios_admin:spec.action.fertilizing') },
  ];

  const spec: ScenarioSpec = {
    specVersion: 1,
    scenarioId: `${template.slug}_v${template.version || 1}`,
    scenarioTemplateId: template.scenarioTemplateId,
    version: template.version || 1,
    title: template.title,
    slug: template.slug,
    kind: template.kind,
    allowRetroEdit: template.allowRetroEdit !== false,
    metadata: { tags: [template.kind], importedFromSeed: true },
    pedagogy: {
      learningObjectives: [],
      targetAudience: 'beginner',
      requireReferenceComparison: false,
    },
    generationPolicy: { mode: 'sim' },
    actionsCatalog,
    world: {
      startDate: String(world.startDate || world.start_date || ''),
      timezone: String(world.timezone || 'UTC'),
      durationDays: Number(template.durationDays || world.maxDays || 0),
      expectedEvents: Number(world.expectedEvents ?? 0),
      sessionConfig: world.sessionConfig || {},
      weatherSeries: (world.weatherSeries || world.weather_series || []) as ScenarioSpecWeatherDay[],
      fields: [{ id: fieldId, name: 'Scenario field' }],
      plots: plots.map((plot) => ({ ...plot, fieldId: plot.fieldId || fieldId })),
      beds,
      plantings,
      structuralObjects: (world.structuralObjects || []) as unknown[],
      goals: template.goals || [],
      scoring: template.scoring || { tiers: [{ tier: 3, minPassed: (template.goals || []).length }] },
      comparison: { metrics: comparisonMetrics },
      narrative: (template.narrative || {}) as ScenarioSpecWorld['narrative'],
    },
    // Награды (лесенка сортов + монеты) — часть содержания шаблона, авторинг
    // правит их в JSON spec, мэппер генератора пробрасывает как есть.
    rewards: template.rewards ?? null,
    // Экономика прохождения едет как есть, тем же путём, что и награды: мэппер
    // генератора пробрасывает её в шаблон, publish пишет в каталог.
    economy: template.economy ?? null,
    branches,
  };

  if (bedIds.length === 0) {
    notes.push({ severity: 'error', message: i18n.t('scenarios_admin:spec.notes.noBeds') });
  }

  return { spec, notes };
};
